import { queryString } from '@shared/hooks/recoil-query';
import { api_ } from '@shared/plugIn/axios';
import { LowerRole } from '@shared/types';
import _ from 'lodash';

import {
  ShopOrder,
  ShopOrderCancel,
  ShopOrderCreateDto,
  ShopOrderManualRefundTarget,
  ShopOrderUpdate,
  StoreRole,
} from './shopOrder.interface';

const vaildRolesCreateShopOrder = [LowerRole.StoreCustomer, LowerRole.StoreGuest];

export const createShopOrder = async (
  shopOrderDto: ShopOrderCreateDto,
  storeRole: StoreRole,
) => {
  if (!_.includes(vaildRolesCreateShopOrder, storeRole)) {
    throw new Error(`${storeRole} role can't use this api`);
  } else {
    const postShopOrderResponse = await api_.post<ShopOrder>(
      `/${storeRole}/shop-order/create`,
      shopOrderDto,
    );
    return postShopOrderResponse;
  }
};

export const verifyShopOrderById = async (
  id: string,
  storeRole: StoreRole,
  receiptId?: string,
) => {
  if (!_.includes(vaildRolesCreateShopOrder, storeRole)) {
    throw new Error(`${storeRole} role can't use this api`);
  } else {
    const { data: isVerify } = await api_.get<boolean>(
      `${storeRole}/shop-order/success/${id}?${queryString({ receiptId })}`,
    );
    return isVerify;
  }
};

export const deleteShopOrderById = async (id: string, storeRole: StoreRole) => {
  if (!_.includes(vaildRolesCreateShopOrder, storeRole)) {
    throw new Error(`${storeRole} role can't use this api`);
  } else {
    return await api_.delete<ShopOrder>(`${storeRole}/shop-order/delete/${id}`);
  }
};

//* shopOrder update
export const updateShopOrder = async (
  shopOrderUpdate: ShopOrderUpdate,
  role: LowerRole,
) => {
  if (role === LowerRole.Admin) {
    const { data } = await api_.patch(
      `/${role}/shop-order/${shopOrderUpdate._id}`,
      shopOrderUpdate,
    );
    return { data };
  } else {
    const { data } = await api_.patch(
      `/${role}/shop-order/patch-detail/${shopOrderUpdate._id}`,
      shopOrderUpdate,
    );
    return { data };
  }
};

//* 주문 알림 재발송 (입금안내/결제완료/배송완료) — 게스트 번호 교정 후 재발송 용도
//* 발송 함수가 내부에서 user를 다시 populate하므로 교정된 번호로 나간다.
//* waiting: Waiting(전화주문)=고정계좌 안내 / NotPaid+vBank=저장된 가상계좌 안내, 그 외 상태 400.
//* paid: Paid/Confirmed만, delivered: Delivered만, 취소 주문은 400. 구서버 미배포 시 404.
export type ShopOrderAlertType = 'waiting' | 'paid' | 'delivered';

export const resendShopOrderAlert = async (
  shopOrderId: string,
  type: ShopOrderAlertType,
  role: LowerRole,
) => {
  const { data } = await api_.post(`${role}/shop-order/resend-alert/${shopOrderId}`, {
    type,
  });
  return data;
};

//* admin 용 주문 상품 교체 — 결제 후 상위 상품으로 변경하고 차액을 수기 수취한 경우의 정정 처리
//* 백엔드가 orderDetail.shopItem + shopItemEmbed(생성 시와 동일 매핑) + paymentDetail.paymentByCash(+=차액)를
//* 원자적으로 갱신한다. 구서버 미배포 시 404. (스펙: docs/shop-order-change-item-spec.md)
export interface ShopOrderItemChange {
  shopItemId: string; // 교체할 shopItem._id (주문의 bugoBrand·shop에서 판매 가능한 상품)
  additionalPaymentByCash: number; // 추가로 받은 차액(원), 0 이상
  memo?: string; // 수취 경위 메모 (ex. "가상계좌 이체 35,000원 수취")
}

export const adminChangeShopOrderItem = async (
  shopOrderId: string,
  dto: ShopOrderItemChange,
) => {
  const { data } = await api_.patch<ShopOrder>(
    `admin/shop-order/change-item/${shopOrderId}`,
    dto,
  );
  return data;
};

//* ───── 면세계산서 발행 요청 · 수기환불(계좌이체) 요청 — seller 접수 → admin 처리 대기열 ─────
//* 가상계좌·무통장(계좌 입금) 결제 전용. 상태머신: 없음 → Requested → Done|Rejected.
//* Requested/Rejected 상태에선 재요청이 기존 요청을 덮어쓰고(오타 교정), Done이면 400.
//* 처리(PATCH)는 admin 전용 — Requested에서만 가능, 경합 시 409. 구서버 미배포 시 404.
//* (스펙: docs/shop-order-seller-request-spec.md)

export interface ShopOrderTaxInvoiceRequestDto {
  bizRegImage?: string; // 선업로드한 image._id (postImage) — 사진만 오는 경우도 수용
  bizNumber?: string;
  email?: string; // 발행본 수신 이메일
  requestNote?: string;
}

export const requestShopOrderTaxInvoice = async (
  shopOrderId: string,
  dto: ShopOrderTaxInvoiceRequestDto,
  role: LowerRole,
) => {
  const { data } = await api_.post<ShopOrder>(
    `${role}/shop-order/tax-invoice-request/${shopOrderId}`,
    dto,
  );
  return data;
};

export interface ShopOrderManualRefundRequestDto {
  account: { bank: string; accountNumber: string; holderName: string };
  target?: ShopOrderManualRefundTarget; // 기본 Customer
  amount?: number; // 기본 paymentDetail.paymentByCash, 1 이상 정수
  requestNote?: string;
}

export const requestShopOrderManualRefund = async (
  shopOrderId: string,
  dto: ShopOrderManualRefundRequestDto,
  role: LowerRole,
) => {
  const { data } = await api_.post<ShopOrder>(
    `${role}/shop-order/manual-refund-request/${shopOrderId}`,
    dto,
  );
  return data;
};

export type ShopOrderSellerRequestKind = 'taxInvoiceRequest' | 'manualRefund';

export interface ShopOrderSellerRequestProcessDto {
  status: 'Done' | 'Rejected';
  cashReceiptCanceled?: boolean; // 면세계산서 전용 — true면 cashReceiptCanceledAt 기록
  resultNote?: string; // 국세청 승인번호 / 이체 메모 / 반려 사유
}

export const processShopOrderSellerRequest = async (
  shopOrderId: string,
  kind: ShopOrderSellerRequestKind,
  dto: ShopOrderSellerRequestProcessDto,
) => {
  const path =
    kind === 'taxInvoiceRequest' ? 'tax-invoice-request' : 'manual-refund-request';
  const { data } = await api_.patch<ShopOrder>(
    `admin/shop-order/${path}/${shopOrderId}`,
    dto,
  );
  return data;
};

//* 처리 되돌리기 — 실수로 완료/반려 처리한 건을 다시 Requested 대기로 (admin 전용).
//* 백엔드가 부수효과를 정확히 역전한다: 계좌환불 Done이 취소확정까지 했던 건
//* (manualRefund.cancelFinalized)이면 주문 Canceled→CancelRequested 복귀 + 포인트 회수.
//* 도장만 찍힌 Done은 도장만 제거. 이미 나간 취소알림은 회수 불가(재처리 시 재발송).
//* 이미 대기·요청 없음은 400, 경합 409, 구서버 400/404 → 호출부에서 안내.
//* (스펙: docs/shop-order-seller-request-spec.md · "처리 되돌리기")
export const revertShopOrderSellerRequest = async (
  shopOrderId: string,
  kind: ShopOrderSellerRequestKind,
) => {
  const path =
    kind === 'taxInvoiceRequest' ? 'tax-invoice-request' : 'manual-refund-request';
  const { data } = await api_.patch<ShopOrder>(
    `admin/shop-order/${path}/${shopOrderId}`,
    { status: 'Requested' },
  );
  return data;
};

export const cancelShopOrder = async (shopOrder: ShopOrderCancel, role: LowerRole) => {
  const { data } = await api_.patch(
    `${role}/shop-order/cancel/${shopOrder._id}`,
    shopOrder,
  );
  return { data };
};

//* store-seller용 전화주문생성
export const createWaitingOrder = async (
  shopOrder: ShopOrderCreateDto,
  role: LowerRole,
) => {
  const { data } = await api_.post<ShopOrder>(
    `${role}/shop-order/create-waiting`,
    shopOrder,
  );
  return data;
};

//* store-seller용 입금완료처리
export const patchOrderWaitingToPaid = async (shopOrderId: string, role: LowerRole) => {
  const { data } = await api_.patch<ShopOrder>(
    `${role}/shop-order/waiting-paid/${shopOrderId}`,
  );
  return data;
};

//*admin 용 shopOrder account처리
export const accountShopOrderList = async (
  shopOrderIdList: string[],
  accountDate?: Date,
  options?: { accountedAsEtc?: boolean },
) => {
  const now = accountDate || new Date();
  const accountUpdateShopOrder: Record<string, unknown> = {
    'paymentDetail.isAccounted': true,
    'paymentDetail.accountedDate': now,
  };
  if (options?.accountedAsEtc) {
    accountUpdateShopOrder['paymentDetail.accountedAsEtc'] = true;
  }
  const { data } = await api_.patch<ShopOrder[]>(`admin/shop-order/with-body`, {
    update: accountUpdateShopOrder,
    filter: {
      _id: { $in: shopOrderIdList },
    },
  });
  return data;
};

const CHUNK_SIZE = 1000;

//*admin 용 shopOrder rebate처리 (3000건 이상 시 load 에러 방지를 위해 1000건씩 분할 요청)
export const rebateShopOrderList = async (
  shopOrderIdList: string[],
  rebateDate?: Date,
) => {
  const now = rebateDate || new Date();
  const rebateUpdateShopOrder = {
    'paymentDetail.isRebated': true,
    'paymentDetail.rebatedDate': now,
  };

  const chunks = _.chunk(shopOrderIdList, CHUNK_SIZE);
  const results: ShopOrder[][] = [];

  for (const chunk of chunks) {
    const { data } = await api_.patch<ShopOrder[]>(`admin/shop-order/with-body`, {
      update: rebateUpdateShopOrder,
      filter: {
        _id: { $in: chunk },
      },
    });
    results.push(Array.isArray(data) ? data : []);
  }

  return results.flat();
};

//*admin 용 shopOrder 정산취소
export const cancelAccountShopOrderList = async (shopOrderIdList: string[]) => {
  const { data } = await api_.patch<ShopOrder[]>(`admin/shop-order/with-body`, {
    update: {
      'paymentDetail.isAccounted': false,
      'paymentDetail.accountedDate': null,
    },
    filter: {
      _id: { $in: shopOrderIdList },
    },
  });
  return data;
};

//*admin 용 shopOrder 상조정산취소
export const cancelRebateShopOrderList = async (shopOrderIdList: string[]) => {
  const { data } = await api_.patch<ShopOrder[]>(`admin/shop-order/with-body`, {
    update: {
      'paymentDetail.isRebated': false,
      'paymentDetail.rebatedDate': null,
    },
    filter: {
      _id: { $in: shopOrderIdList },
    },
  });
  return data;
};

export const sendVBankAlimUser = async (id: string, vBank: string) => {
  const result = await api_.post(`public/shop-order/send-v-bank-message/${id}`, {
    vBank,
  });
  return result;
};

//*admin 용 shopOrder rebate처리
export const superRebateShopOrderList = async (shopOrderIdList: string[]) => {
  const now = new Date(2024, 3, 15);
  const rebateUpdateShopOrder = {
    'paymentDetail.rebatedDate': now,
  };
  const { data } = await api_.patch<ShopOrder[]>(`admin/shop-order/with-body`, {
    update: rebateUpdateShopOrder,
    filter: {
      _id: { $in: shopOrderIdList },
    },
  });
  return data;
};
